import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Provider } from 'react-redux';
import store from 'features/workspace/store';
import { UserTypeEnum } from 'enums';
import { withAuth } from 'features/auth/withAuth';
import { useAuthUser } from 'features/auth/AuthUserContext';
import Tracker from '@openreplay/tracker';
import { withTranslations } from 'utils/withTranslations';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useSelector } from 'react-redux';

// Dynamically import heavy components and contexts
const Page = dynamic(() => import('components/Workspace/Page'), { ssr: false });
const WorkspaceFontsProvider = dynamic(
  () => import('contexts/useWorkspaceFontsContext').then((mod) => mod.WorkspaceFontsProvider),
  { ssr: false },
);

// Client-only component for Intercom NPS logic -- disabled (Intercom removed)
// const NPSLogic = () => {
//   const { user } = useAuthUser();
//
//   useEffect(() => {
//     const openNps = () => {
//       const npsOpened = localStorage.getItem('npsOpened');
//       if (user?.tp_user === UserTypeEnum.TRIAL && !npsOpened) {
//         if (typeof window !== 'undefined' && typeof Intercom !== 'undefined') {
//           Intercom('startSurvey', 35747565);
//         }
//         localStorage.setItem('npsOpened', true);
//       }
//     };
//     const timeout = setTimeout(openNps, 1000 * 60 * 10);
//     return () => clearTimeout(timeout);
//   }, [user]);
//
//   return null;
// };

const UndoStackDisplay = () => {
  const undoStack = useSelector((state) => state.canvas.undoStack || []);
  const undoStackLength = undoStack.length;

  // Calculate binary size in bytes
  const getBinarySize = (obj) => {
    if (!obj) return 0;
    try {
      // UTF-8 byte length
      return new TextEncoder().encode(JSON.stringify(obj)).length;
    } catch {
      return 0;
    }
  };
  const undoStackSize = getBinarySize(undoStack);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 100,
        left: 10,
        background: '#333',
        color: '#fff',
        padding: '8px 16px',
        borderRadius: 8,
        fontSize: 14,
        zIndex: 9999,
        fontFamily: 'monospace',
        border: '1px solid #555',
      }}
    >
      Undo Stack: {undoStackLength} <br />
      Size: {undoStackSize} bytes
    </div>
  );
};

const MemoryStats = () => {
  const [stats, setStats] = useState('');
  useEffect(() => {
    const updateStats = () => {
      if (performance?.memory) {
        const percent = Math.round(
          (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100,
        );
        const usedMB = Math.round(performance.memory.usedJSHeapSize / 1048576);
        const limitMB = Math.round(performance.memory.jsHeapSizeLimit / 1048576);
        setStats(`${percent}% (${usedMB}MB / ${limitMB}MB)`);
      }
    };
    updateStats();
    const interval = setInterval(updateStats, 2000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 45,
        left: 10,
        background: '#333',
        color: '#fff',
        padding: '8px 16px',
        borderRadius: 8,
        fontSize: 14,
        zIndex: 9999,
        fontFamily: 'monospace',
        border: '1px solid #555',
      }}
    >
      JS Heap: {stats}
    </div>
  );
};

function WorkspacePage() {
  const { user } = useAuthUser();
  const router = useRouter();
  // commented for performance issues
  useEffect(() => {
    if (process?.env?.OPENREPLAY_PROJECT_KEY) {
      if (typeof window !== 'undefined' && !window.trackerInstance) {
        window.trackerInstance = new Tracker({
          projectKey: process.env.OPENREPLAY_PROJECT_KEY,
          ingestPoint: 'https://openreplay.mindthegraph.com/ingest',
          canvas: {
            disableCanvas: false,
            preserveDrawingBuffer: true,
          },
          onSessionStart: (session) => {
            if (!window.location.href.includes('workspace')) {
              session.cancel();
            }
          },
        });
        const userIdentifier = user?.email;
        if (
          userIdentifier &&
          (userIdentifier.includes('mailinator') || userIdentifier.includes('cactusglobal'))
        ) {
          console.log('User is a test user or using mailinator/cactusglobal email.');
          // console.log('Session Not started for test user or mailinator/cactusglobal email.');
          return;
        }
        window.trackerInstance.start();

        if (userIdentifier) {
          window.trackerInstance.setUserID(userIdentifier);
        }
      }
    } else {
      console.log('P key not found');
    }

    return () => {
      if (typeof window !== 'undefined' && window.trackerInstance) {
        try {
          window.trackerInstance.stop();
        } catch (error) {
          // console.error('Failed to stop OpenReplay tracker', error);
        }
        window.trackerInstance = null;
      }
    };
  }, [user]);
  useEffect(() => {
    const handleContextmenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextmenu);
    return () => document.removeEventListener('contextmenu', handleContextmenu);
  }, []);

  return (
    <Provider store={store}>
      <WorkspaceFontsProvider>
        <Page />
        {/* <NPSLogic /> */}
        {router.query.stats === 'true' && (
          <>
            <MemoryStats />
            <UndoStackDisplay />
          </>
        )}
      </WorkspaceFontsProvider>
    </Provider>
  );
}

export default withAuth(WorkspacePage);
export const getServerSideProps = withTranslations(['pricing', 'common']);
